import { useLocation, useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import { useState, useEffect, useRef, useCallback } from "react"
import { Button, Box, CircularProgress, Typography, Grid } from "@mui/material"
import {
  ArrowBack,
  Download,
  ErrorOutline,
  RestartAlt,
  PictureAsPdf,
  CheckCircleOutline
} from "@mui/icons-material"
import ReportRegenerateModal from "../../components/ReportRegenerateModal"

import { Viewer, Worker } from "@react-pdf-viewer/core"
import "@react-pdf-viewer/core/lib/styles/index.css"
import "@react-pdf-viewer/default-layout/lib/styles/index.css"

const baseURL = process.env.REACT_APP_BASE_URL

const GetReport = () => {
  const { barcode } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [downloadLoad, setDownloadLoad] = useState(false)
  const [pdfBlob, setPdfBlob] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [showRegenerateModal, setShowRegenerateModal] = useState(false)
  const [regeneratedAt, setRegeneratedAt] = useState("")
  const pdfBlobRef = useRef(null)
  const canGoBack = location.key !== "default"

  const fetchPdf = useCallback(async (regenerate = false) => {
    try {
      if (regenerate) {
        setIsRegenerating(true)
      } else {
        setIsLoading(true)
      }
      const token = localStorage.getItem("token")

      const response = await axios.get(`${baseURL}report/download/${barcode}`, {
        responseType: "blob",
        headers: {
          Accept: "application/pdf",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        timeout: 60000,
        params: regenerate ? { regenerate: true } : {}
      })

      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }))

      if (pdfBlobRef.current) {
        URL.revokeObjectURL(pdfBlobRef.current)
      }
      pdfBlobRef.current = blobUrl
      setPdfBlob(blobUrl)
      setShowRegenerateModal(false)
      if (regenerate) {
        setRegeneratedAt(new Date().toLocaleString())
      }
    } catch (error) {
      console.error("PDF fetch error:", error)
      toast.error("Failed to load PDF")
    } finally {
      setIsLoading(false)
      setIsRegenerating(false)
    }
  }, [barcode])

  useEffect(() => {
    fetchPdf(false)

    return () => {
      if (pdfBlobRef.current) {
        URL.revokeObjectURL(pdfBlobRef.current)
      }
    }
  }, [fetchPdf])

  const handlePrint = async () => {
    setDownloadLoad(true)
    try {
      const link = document.createElement("a")
      link.href = pdfBlob
      link.download = `report_${barcode}.pdf`
      link.click()
    } catch (error) {
      console.error("Download error:", error)
      toast.error("Failed to download the report")
    } finally {
      setDownloadLoad(false)
    }
  }

  return (
    <Box sx={{ backgroundColor: "white", minHeight: "100vh", py: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          maxWidth: 1180,
          mx: "auto",
          px: { xs: 2, md: 3 },
          mb: 3
        }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            backgroundColor: "#f7f8fb",
            border: "1px solid #e4e7ee",
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
            px: { xs: 2, md: 3 },
            py: 2
          }}>
          <Grid item xs={12} md>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
              {canGoBack && (
                <Button
                  startIcon={<ArrowBack />}
                  onClick={() => navigate(-1)}
                  sx={{
                    border: "1px solid #d8dde6",
                    borderRadius: "999px",
                    color: "#3f4a5a",
                    fontWeight: 800,
                    minHeight: 38,
                    px: 1.75,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "#eef2f7",
                      borderColor: "#aeb6c3"
                    }
                  }}
                >
                  Back
                </Button>
              )}
              <PictureAsPdf sx={{ color: "#3f4a5a", fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                  Report Preview
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Barcode: {barcode}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md="auto" sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
            <Button
              startIcon={downloadLoad ? <CircularProgress color="inherit" size={18} /> : <Download />}
              variant="contained"
              className="btn_bg_ter"
              sx={{
                borderRadius: "8px",
                minHeight: 42,
                px: 2.25,
                whiteSpace: "nowrap",
                fontWeight: 700,
                boxShadow: "0 8px 16px rgba(63, 74, 90, 0.18)"
              }}
              onClick={handlePrint}
              disabled={!pdfBlob || downloadLoad || isLoading}>
              Download
            </Button>
            <Button
              startIcon={isRegenerating ? <CircularProgress color="inherit" size={18} /> : <RestartAlt />}
              variant="contained"
              className="btn_bg_secondery"
              sx={{
                borderRadius: "8px",
                minHeight: 42,
                px: 2.25,
                whiteSpace: "nowrap",
                fontWeight: 700,
                boxShadow: "0 8px 16px rgba(63, 74, 90, 0.18)"
              }}
              onClick={() => setShowRegenerateModal(true)}
              disabled={isLoading || isRegenerating}>
              Regenerate
            </Button>
          </Grid>
        </Grid>
      </Box>

      {regeneratedAt && (
        <Box
          sx={{
            maxWidth: 900,
            mx: "auto",
            mb: 2,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            backgroundColor: "#edf7f1",
            border: "1px solid #cce8d6",
            color: "#25613b",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600
          }}>
          <CheckCircleOutline fontSize="small" />
          Regenerated just now. This is now the saved original report.
        </Box>
      )}

      <div>
        <PDFViewer pdfBlob={pdfBlob} barcode={barcode} isLoading={isLoading} />
      </div>
      <ReportRegenerateModal
        show={showRegenerateModal}
        onHide={() => setShowRegenerateModal(false)}
        onRegenerate={() => fetchPdf(true)}
        loading={isRegenerating}
      />
    </Box>
  )
}

const PDFViewer = ({ pdfBlob, isLoading, error }) => {
  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        maxWidth: "900px",
        margin: "0 auto",
        backgroundColor: "#fff"
      }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 18px",
          backgroundColor: "#f7f8fb",
          borderBottom: "1px solid #e0e0e0"
        }}>
        {/* Navigation controls */}

        {/* Download button */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PictureAsPdf sx={{ color: "#3f4a5a" }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
            Report
          </Typography>
        </Box>
      </Box>

      {/* PDF Content */}
      <Box
        sx={{
          minHeight: "500px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#525659",
          position: "relative"
        }}>
        {isLoading && (
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading PDF...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ textAlign: "center", color: "error.main" }}>
            <ErrorOutline fontSize="large" />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Failed to load PDF
            </Typography>
          </Box>
        )}

        {pdfBlob && (
          <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js`}>
            <Viewer
              fileUrl={pdfBlob}

            // plugins={[defaultLayoutPluginInstance]}
            />
          </Worker>
        )}
      </Box>
    </Box>
  )
}

export default GetReport
